import { RecordScreenCard } from "@/lib/Components/Screens/RecordScreen/Cards/RecordScreenCard";
import { SortableList } from "@/lib/Components/Sortable/SortableList";
import {
  useGqlMutation,
  useGqlQuery,
  useSuspenseGqlQuery,
} from "@/lib/GraphQLCodegen/fetcher";
import {
  blogPostTripArticleListQuery,
  createBlogPostTripArticleMutation,
  deleteBlogPostTripArticleMutation,
  sortBlogPostTripArticleMutation,
} from "@/app/Cities/Trips/GraphQL/blogPostTripArticleQueries";
import {
  BlogPostListItem,
  blogPostListQuery,
} from "@/app/Blogs/GraphQL/blogPostListQuery";
import { useQueryClient } from "@tanstack/react-query";
import { Spinner } from "@/lib/Components/Layout/Loaders/Spinner";
import { PlusIcon } from "@heroicons/react/20/solid";
import { cn } from "@/lib/utils";
import {
  SortableItem,
  SortableItemDragHandle,
} from "@/lib/Components/Sortable/SortableItem";
import { IconButton } from "@/lib/Components/Button/IconButton";
import { TrashIcon, ArrowsUpDownIcon } from "@heroicons/react/24/outline";
import { useEffect, useState, memo, useCallback } from "react";
import { EmptyState } from "@/lib/Components/EmptyState/EmptyState";
import { BookMinusIcon, BookOpenIcon } from "lucide-react";

type TripDirection = {
  originCityId: string;
  destinationCityId: string;
};

export function TripArticleManager({
  originCityId,
  destinationCityId,
}: TripDirection) {
  const { data } = useSuspenseGqlQuery(blogPostTripArticleListQuery, {
    originCityId,
    destinationCityId,
  });
  const queryClient = useQueryClient();
  const [items, setItems] = useState(data.blogPostTripArticles);

  useEffect(() => {
    setItems(data.blogPostTripArticles);
  }, [data.blogPostTripArticles.length]);

  const { mutateAsync } = useGqlMutation(sortBlogPostTripArticleMutation);

  const { mutateAsync: deleteItem } = useGqlMutation(
    deleteBlogPostTripArticleMutation,
    {
      onSuccess: () => {
        queryClient.invalidateQueries({
          predicate: (q) => q.queryKey.includes("BlogPostTripArticleListQuery"),
        });
      },
    },
  );

  const handleSort = useCallback(
    (articles: typeof items) => {
      setItems(articles);
      return mutateAsync({
        ids: articles.map((item) => item.id),
      });
    },
    [mutateAsync],
  );

  const handleDelete = useCallback(
    (id: string) => deleteItem({ id }),
    [deleteItem],
  );

  const { data: blogPostsData } = useGqlQuery(blogPostListQuery, {
    first: 200,
    publishedOnly: true,
  });

  const posts = blogPostsData?.blogPosts.data ?? [];
  const attachedIds = new Set(
    data.blogPostTripArticles
      .map((a) => a.blog_post_id)
      .filter((id): id is string => id !== null && id !== undefined),
  );

  return (
    <div className="space-y-10">
      <RecordScreenCard
        title={
          <div className="flex items-center gap-2 text-indigo-700">
            <BookOpenIcon className="h-5 w-5" />
            <span>Attached Articles</span>
            <span className="ml-1 rounded-full bg-indigo-100 px-2.5 py-0.5 text-sm font-medium text-indigo-700">
              {data.blogPostTripArticles.length}
            </span>
          </div>
        }
      >
        {items.length === 0 ? (
          <EmptyState
            Icon={BookMinusIcon}
            header="No articles are linked to this direction"
            subheader="Add articles below to show them on this route's deal pages"
            className="rounded-lg bg-gray-50 py-12"
          />
        ) : (
          <div className="rounded-lg bg-gray-50 p-4">
            {items.length > 1 ? (
              <div className="mb-4 flex items-center gap-2 text-sm text-gray-500">
                <ArrowsUpDownIcon className="h-4 w-4" />
                <span>Drag to reorder articles</span>
              </div>
            ) : null}
            <SortableList
              items={items}
              onChange={handleSort}
              renderItem={(item) => (
                <SortableItem id={item.id}>
                  <div className="group relative w-full overflow-hidden rounded-lg border border-gray-200 bg-white">
                    <div className="invisible absolute left-0 top-0 group-hover:visible">
                      <IconButton
                        Icon={TrashIcon}
                        onClick={() => handleDelete(item.id)}
                        tooltip="Detach"
                        className="hover:bg-red-50"
                      />
                    </div>
                    <div className="flex items-center">
                      <div className="h-20 w-20 shrink-0">
                        {item.blogPost?.hero_image?.url ? (
                          <img
                            loading="lazy"
                            className="h-full w-full object-cover"
                            src={item.blogPost.hero_image.url}
                            alt={item.blogPost.title}
                          />
                        ) : null}
                      </div>
                      <div className="flex-1 p-3">
                        <p className="line-clamp-2 font-medium text-gray-800">
                          {item.blogPost?.title ?? "Untitled"}
                        </p>
                      </div>
                    </div>
                    <SortableItemDragHandle />
                  </div>
                </SortableItem>
              )}
            />
          </div>
        )}
      </RecordScreenCard>

      <RecordScreenCard
        title={
          <div className="flex items-center gap-2 text-emerald-700">
            <BookOpenIcon className="h-5 w-5" />
            <span>Published Articles</span>
          </div>
        }
      >
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {posts.map((post) => (
            <MemoizedArticleBlock
              originCityId={originCityId}
              destinationCityId={destinationCityId}
              key={post.id}
              post={post}
              disabled={attachedIds.has(post.id)}
            />
          ))}
        </div>
      </RecordScreenCard>
    </div>
  );
}

function ArticleBlock({
  post,
  disabled,
  originCityId,
  destinationCityId,
}: TripDirection & {
  post: BlogPostListItem;
  disabled: boolean;
}) {
  const queryClient = useQueryClient();
  const { mutateAsync, isPending } = useGqlMutation(
    createBlogPostTripArticleMutation,
    {
      onSuccess: () => {
        return queryClient.invalidateQueries({
          predicate: (q) => q.queryKey.includes("BlogPostTripArticleListQuery"),
        });
      },
    },
  );

  const handleClick = useCallback(async () => {
    if (isPending || disabled) return;

    await mutateAsync({
      input: {
        blog_post_id: post.id,
        destination_city_id: destinationCityId,
        origin_city_id: originCityId,
      },
    });
  }, [
    post.id,
    disabled,
    isPending,
    mutateAsync,
    destinationCityId,
    originCityId,
  ]);

  return (
    <button
      disabled={isPending || disabled}
      onClick={handleClick}
      className={cn(
        "group relative overflow-hidden rounded-lg border",
        disabled
          ? "border-emerald-200 bg-emerald-50"
          : "shadow-xs border-gray-200 bg-white hover:border-indigo-200 hover:shadow-md",
        (isPending || disabled) && "cursor-not-allowed",
      )}
    >
      <div className="aspect-video w-full overflow-hidden">
        {post.hero_image?.url ? (
          <img
            loading="lazy"
            className={cn(
              "h-full w-full object-cover",
              !(isPending || disabled) &&
                "transform-gpu duration-300 group-hover:scale-105",
              (isPending || disabled) && "opacity-80",
            )}
            src={post.hero_image.url}
            alt={post.title}
          />
        ) : (
          <div className="h-full w-full bg-zinc-100" />
        )}
      </div>

      {isPending ? (
        <div className="absolute inset-0 flex items-center justify-center bg-black/10">
          <Spinner className="text-white" />
        </div>
      ) : (
        <div className="absolute inset-0 flex items-center justify-center bg-black/0 transition-colors duration-300 group-hover:bg-black/30">
          {!disabled ? (
            <div
              className={cn(
                "flex items-center justify-center gap-2 rounded-full bg-white px-3 py-1 shadow-sm",
                "opacity-0 transition-opacity duration-200 group-hover:opacity-100",
              )}
            >
              <PlusIcon className="h-4 w-4 text-indigo-600" />
              <span className="text-xs font-medium text-indigo-600">Add</span>
            </div>
          ) : (
            <div className="rounded-full bg-emerald-100 px-3 py-1 text-xs font-medium text-emerald-700">
              Added
            </div>
          )}
        </div>
      )}

      <div className={cn("p-2", disabled ? "bg-emerald-50" : "bg-white")}>
        <p
          className={cn(
            "line-clamp-2 text-sm",
            disabled ? "text-emerald-700" : "text-gray-700",
          )}
        >
          {post.title}
        </p>
      </div>
    </button>
  );
}

const MemoizedArticleBlock = memo(ArticleBlock);
